import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon.js";
import "./Tabs.css";

/**
 * Tabs, with a state per tab.
 *
 * Three screens had hand-rolled a tab bar with `role="tab"` and none of them
 * implemented arrow-key navigation, which is the part of the WAI-ARIA pattern
 * people actually rely on.
 *
 * The state dot is what makes this carry the PIM: the channel tabs show
 * whether each platform is ready *before* you open it, so the strip is a
 * summary rather than a set of doors.
 */

export type TabState = "ready" | "partial" | "breach" | "unsourced";

export interface Tab {
  id: string;
  label: string;
  icon?: IconName;
  /** A count, shown as a quiet badge. */
  badge?: number;
  /** Readiness, shown as a dot and read aloud in the accessible name. */
  state?: TabState;
  /** What the dot means, for screen readers: "3 requirements missing". */
  stateLabel?: string;
}

export interface TabsProps {
  tabs: Tab[];
  value: string;
  onChange: (id: string) => void;
  variant?: "underline" | "pill";
  children?: ReactNode;
}

export function Tabs({ tabs, value, onChange, variant = "underline", children }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ overflowing: false, atEnd: false });

  /*
   * Whether there are tabs beyond the right edge.
   *
   * The scrollbar is hidden, so a cut-off tab is the only clue the strip
   * scrolls at all — and on its own it reads as a rendering fault. This drives
   * the fade in the CSS, which CSS cannot decide for itself.
   */
  const measure = useCallback(() => {
    const node = listRef.current;
    if (!node) return;
    const overflowing = node.scrollWidth > node.clientWidth + 1;
    const atEnd = node.scrollLeft + node.clientWidth >= node.scrollWidth - 1;
    setEdge((previous) =>
      previous.overflowing === overflowing && previous.atEnd === atEnd ? previous : { overflowing, atEnd },
    );
  }, []);

  useEffect(() => {
    measure();
    const node = listRef.current;
    if (!node) return;

    // Re-measured on resize as well as scroll: the strip fits or does not fit
    // depending on the window, not only on where it has been scrolled.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    node.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer.disconnect();
      node.removeEventListener("scroll", measure);
    };
  }, [measure, tabs.length]);

  /*
   * Arrow keys move between tabs, Home and End jump to the ends.
   *
   * This is the WAI-ARIA tabs pattern, and it is the reason this component
   * exists rather than three near-identical bars: a keyboard user expects
   * arrows here, and Tab to leave the strip entirely.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.id === value);
    if (index < 0) return;

    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else return;

    event.preventDefault();
    onChange(tabs[next]!.id);
    // Move focus with selection, so the newly selected tab is where the next
    // keystroke lands.
    listRef.current?.querySelectorAll<HTMLButtonElement>("[role='tab']")[next]?.focus();
  };

  return (
    <div className={`ui-tabs ui-tabs--${variant}`}>
      <div
        className={[
          "ui-tabs__list",
          edge.overflowing ? "is-overflowing" : "",
          edge.atEnd ? "is-at-end" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="tablist"
        ref={listRef}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => {
          const selected = tab.id === value;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={selected}
              // Only the selected tab has a panel in the DOM, and only when the
              // caller passed children; pointing elsewhere is an invalid reference.
              aria-controls={selected && children ? `panel-${tab.id}` : undefined}
              // Roving tabindex: one stop for the whole strip, arrows within.
              tabIndex={selected ? 0 : -1}
              className={`ui-tabs__tab ${selected ? "is-active" : ""}`}
              onClick={() => onChange(tab.id)}
            >
              {tab.icon && <Icon name={tab.icon} size={15} />}
              <span>{tab.label}</span>

              {tab.state && (
                <span
                  className={`ui-tabs__dot ui-tabs__dot--${tab.state}`}
                  // Never colour alone: the meaning is in the accessible name
                  // as well as the hue.
                  role="img"
                  aria-label={tab.stateLabel ?? tab.state}
                />
              )}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ui-tabs__badge">{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {children && (
        <div role="tabpanel" id={`panel-${value}`} aria-labelledby={`tab-${value}`} className="ui-tabs__panel">
          {children}
        </div>
      )}
    </div>
  );
}
